import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Field, Input, MessageBar, MessageBarBody, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { MIN_PASSWORD_LENGTH } from '@vxh/shared';
import { api } from '../api';

const useStyles = makeStyles({
  wrap: { minHeight: '100vh', display: 'grid', placeItems: 'center', ...shorthands.padding('24px'), backgroundColor: tokens.colorNeutralBackground2 },
  card: { width: '100%', maxWidth: '400px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('16px'), ...shorthands.padding('28px') },
  brand: { display: 'flex', alignItems: 'center', ...shorthands.gap('10px') },
  logo: { width: '28px', height: '28px' },
});

/** The page behind the link in an invitation email: choose a password, then carry on to the authenticator set-up on the normal sign-in screen. */
export function AcceptInvitePage() {
  const s = useStyles();
  const qc = useQueryClient();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => api('/auth/invite', { method: 'POST', body: { token, password } }),
    // The person now has a half-finished session (password given): clear the link from the address and let the app show the authenticator set-up.
    onSuccess: () => {
      window.history.replaceState(null, '', '/');
      void qc.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (e) => setError((e as Error).message),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    accept.mutate();
  };
  const mismatch = again.length > 0 && again !== password;
  return (
    <div className={s.wrap}>
      <Card className={s.card}>
        <div className={s.brand}>
          <img src="/favicon.svg" alt="" className={s.logo} />
          <Text size={500} weight="semibold">Voxshift Hub</Text>
        </div>
        <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
          <Text>Welcome. Choose a password for your Voxshift Hub account. Next you will set up an authenticator app on your phone.</Text>
          <Field label="Password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
            <Input type="password" value={password} onChange={(_, d) => setPassword(d.value)} autoComplete="new-password" autoFocus />
          </Field>
          <Field label="Password again" validationState={mismatch ? 'error' : 'none'} validationMessage={mismatch ? 'The two passwords are not the same.' : undefined}>
            <Input type="password" value={again} onChange={(_, d) => setAgain(d.value)} autoComplete="new-password" />
          </Field>
          {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          <Button appearance="primary" type="submit" disabled={accept.isPending || !token || password.length < MIN_PASSWORD_LENGTH || password !== again}>Continue</Button>
        </form>
      </Card>
    </div>
  );
}
